import UserAvatar from "@/components/ui/UserAvatar";
import { useEffect, useState } from "react";
import HistoryPanel from "@/components/common/HistoryPanel";
import { HISTORY_COLORS } from "@/components/ui/HistoryTimeline";
import { CheckCircle2, XCircle, Ban, Bell, Sparkles, History, ListTodo } from "lucide-react";
import api from "@/lib/api";
import { avatarColorForSeed } from "@/lib/avatarColors";
import useAviStream from "@/hooks/useAviStream";

/**
 * AviHistory — timeline verticale des événements clés d'une demande AVI.
 *
 * Reconstruite côté backend à partir du doc (aucune collection dédiée) —
 * la source de vérité reste `avi_requests`.
 */
// iter166 — badges standard.
const KIND_BADGE = {
  created: ["CRÉÉ", HISTORY_COLORS.green], approuvee: ["APPROUVÉE", HISTORY_COLORS.green], refusee: ["REFUSÉE", HISTORY_COLORS.red],
  cancelled: ["ANNULÉE", HISTORY_COLORS.red], reminder: ["RELANCE", HISTORY_COLORS.amber], task_created: ["TÂCHE CRÉÉE", HISTORY_COLORS.blue],
};
const KIND_META = {
  created:   { color: "#EC4899", Icon: Sparkles,     label: "Créée" },
  approuvee: { color: "#4ADE80", Icon: CheckCircle2, label: "Approuvée" },
  refusee:   { color: "#F87171", Icon: XCircle,      label: "Refusée" },
  cancelled: { color: "#9CA3AF", Icon: Ban,          label: "Annulée" },
  reminder:  { color: "#F472B6", Icon: Bell,         label: "Relance" },
  task_created: { color: "#7fa8dd", Icon: ListTodo,  label: "Tâche créée" },
};

function Avatar({ user, size = 26 }) {
  return <UserAvatar user={user} size={size} showPopover={false} />;
}

export default function AviHistory({ aviId, usersById = {} }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get(`/avi/${aviId}/history`);
      setEvents(r.data.items || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { if (aviId) load(); }, [aviId]); // eslint-disable-line react-hooks/exhaustive-deps
  useAviStream({
    onAny: (evt) => {
      if (evt?.avi_id === aviId && evt?.kind !== "message") load();
    },
  });

  return (
    <div className="rounded-xl overflow-hidden"
         style={{ background: "#161616", border: "1px solid #2A2A2A", minHeight: 340 }}
         data-testid="avi-history">
      <div className="px-4 py-3 flex items-center gap-2"
           style={{ borderBottom: "1px solid #2A2A2A" }}>
        <History size={14} style={{ color: "#EC4899" }} />
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
          Chrono · {events.length}
        </div>
      </div>
      <div className="p-4">
        {loading && <div className="text-center text-xs text-muted-foreground py-6">Chargement…</div>}
        {!loading && events.length === 0 && (
          <div className="text-center text-xs text-muted-foreground py-6">Aucun événement.</div>
        )}
        {!loading && events.length > 0 && (
          <HistoryPanel entityType="avi" entityId={aviId} entityLabel="l'AVI" height={480} testId="avi-history" extraEvents={events.map((e, idx) => { const meta = KIND_META[e.kind] || KIND_META.created; const actor = e.actor_id ? usersById[e.actor_id] : null; const target = e.target_user_id ? usersById[e.target_user_id] : null; const targetName = target ? `${target.first_name || ""} ${target.last_name || ""}`.trim() || target.email : null; return { id: `avi-${idx}`, legacy: true, at: e.at, action: e.kind === "created" ? "created" : /cancel|annul/.test(e.kind) ? "cancelled" : /valid|accept/.test(e.kind) ? "validated" : "updated", action_label: (KIND_BADGE[e.kind] || [meta.label.toUpperCase()])[0], summary: `${meta.label}${targetName ? ` → ${targetName}` : ""}${e.comment_type ? ` · ${e.comment_type}` : ""}${e.kind === "task_created" && e.task_reference ? ` · ${e.task_reference}` : ""}`, reason: e.reason || e.note || null, changes: [], author: { id: e.actor_id, name: actor ? `${actor.first_name || ""} ${actor.last_name || ""}`.trim() || actor.email : "—" } }; })} />
        )}
      </div>
    </div>
  );
}
